import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { useTranslation } from "@redotech/redo-i18n";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import { Avatar } from "@redotech/redo-web/arbiter-components/avatars/avatar";
import {
  RedoCommandMenu,
  type RedoCommandMenuItem,
} from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { Flex } from "@redotech/redo-web/flex";
import { getGravatarUrlFromEmail } from "@redotech/redo-web/gravatar-utils";
import { Text } from "@redotech/redo-web/text";
import { useState, type ReactNode } from "react";
import * as navbarCss from "./version-four-navbar.module.css";

export interface VersionFourNavbarDisplayProps {
  customerEmail?: string;
  customerName?: string;
  fontFamily?: string;
  hideNavigation?: boolean;
  leftContent?: ReactNode;
  logoHref?: string;
  logoUrl?: string;
  menuItems: RedoCommandMenuItem[];
  orderName?: string;
  storeName?: string;
  storeCreditAmount?: string;
}

export function VersionFourNavbarDisplay({
  customerEmail,
  customerName,
  fontFamily,
  hideNavigation,
  leftContent,
  logoHref,
  logoUrl,
  menuItems,
  orderName,
  storeName,
  storeCreditAmount,
}: VersionFourNavbarDisplayProps) {
  const { t } = useTranslation();
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const screen = useScreenType();

  return (
    <Flex
      align="center"
      as="header"
      bgColor="primary"
      borderBottomWidth="1px"
      borderColor="secondary"
      borderStyle="solid"
      className={navbarCss.navBar}
      data-testid="navbar"
      data-ui="navbar"
      dir="row"
      justify="space-between"
      position="sticky"
      px={screen === ScreenType.DESKTOP ? "2xl" : "lg"}
      py="lg"
      style={{ fontFamily }}
      top="0"
    >
      <Flex align="center" basis="0" dir="row" gap="lg" grow={1}>
        {leftContent}
        {screen === ScreenType.DESKTOP && orderName && (
          <Text
            className={navbarCss.navBarNoWrap}
            fontSize="sm"
            fontWeight="semibold"
            textColor="primary"
          >
            {t("Order {{orderName}}", { orderName })}
          </Text>
        )}
      </Flex>

      <Flex justify="center" shrink={0}>
        {renderBrand({
          logoHref,
          logoUrl,
          storeName,
          logoAlt: t("Store logo"),
        })}
      </Flex>

      <Flex
        align="center"
        basis="0"
        dir="row"
        gap="sm"
        grow={1}
        justify="flex-end"
      >
        {!!storeCreditAmount && (
          <Flex
            align="center"
            data-ui="store-credit-balance"
            dir="row"
            gap="sm"
          >
            <Text
              className={navbarCss.navBarNoWrap}
              fontSize="xs"
              textColor="primary"
            >
              {t("Store credit balance:")}
            </Text>
            <Flex
              align="center"
              className={navbarCss.storeCreditPill}
              data-ui="store-credit-balance-amount"
              p="sm"
            >
              <Text fontSize="xs" fontWeight="semibold" textColor="primary">
                {storeCreditAmount}
              </Text>
            </Flex>
          </Flex>
        )}
        {!hideNavigation && (
          <>
            <RedoCommandMenu
              anchor={dropdownButtonRef}
              items={menuItems}
              open={dropdownOpen}
              placement="bottom-end"
              setOpen={setDropdownOpen}
              size="sm"
            />

            <Flex
              align="center"
              aria-expanded={dropdownOpen}
              aria-haspopup="menu"
              as="button"
              className={navbarCss.userMenuTrigger}
              gap="sm"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              ref={setDropdownButtonRef}
              type="button"
            >
              <Flex align="center" dir="row" gap="sm">
                <Avatar
                  alt={customerName || customerEmail || ""}
                  imageUrl={
                    customerName
                      ? null
                      : customerEmail
                        ? getGravatarUrlFromEmail(customerEmail)
                        : null
                  }
                  name={customerName}
                  uiSelector="navbar-avatar"
                />
                {screen === ScreenType.DESKTOP && customerName && (
                  <Text
                    className={navbarCss.navBarNoWrap}
                    fontSize="sm"
                    fontWeight="bold"
                  >
                    {customerName}
                  </Text>
                )}
              </Flex>
              <ChevronDownIcon className={navbarCss.chevronIcon} />
            </Flex>
          </>
        )}
      </Flex>
    </Flex>
  );
}

function renderBrand({
  logoHref,
  logoUrl,
  storeName,
  logoAlt,
}: {
  logoHref?: string;
  logoUrl?: string;
  storeName?: string;
  logoAlt: string;
}): ReactNode {
  if (logoUrl) {
    const img = (
      <img
        alt={logoAlt}
        className={navbarCss.navBarLogo}
        id="navbar-logo"
        src={logoUrl}
      />
    );
    return logoHref ? (
      <a href={logoHref} rel="noreferrer" target="_blank">
        {img}
      </a>
    ) : (
      img
    );
  }
  if (!storeName) {
    return null;
  }
  const text = (
    <Text
      className={navbarCss.navBarStoreName}
      fontSize="md"
      fontWeight="semibold"
      textColor="primary"
    >
      {storeName}
    </Text>
  );
  return logoHref ? (
    <a
      className={navbarCss.navBarStoreNameLink}
      href={logoHref}
      rel="noreferrer"
      target="_blank"
    >
      {text}
    </a>
  ) : (
    text
  );
}
